import React, { useState, useEffect } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { FormControl, Select, MenuItem } from "@mui/material";
import { useDispatch } from 'react-redux';
import EventBus from 'eventing-bus';
import { updatePhone } from '../../../store/Phone';
import '../index.css';

const AddAgentModal = ({ togglePhoneModal, isPhoneModal, phones = [], agentId, agentData, onPhoneAssign }) => {
    const dispatch = useDispatch();
    const [selectedInboundPhone, setSelectedInboundPhone] = useState("");
    const [selectedOutboundPhone, setSelectedOutboundPhone] = useState("");
    const [isAssigning, setIsAssigning] = useState(false);
    const [inboundWarning, setInboundWarning] = useState("");
    const [outboundWarning, setOutboundWarning] = useState("");

    // Pre-populate phone selections from agentData when modal opens
    useEffect(() => {
        if (isPhoneModal && phones && phones.length > 0) {
            // Find phones that are assigned to this agent
            const inboundPhone = phones.find(phone => phone.inboundAgentId && phone.inboundAgentId._id === agentId);
            const outboundPhone = phones.find(phone => phone.outboundAgentId && phone.outboundAgentId._id === agentId);

            // Set inbound phone if agent has one assigned
            if (inboundPhone) {
                setSelectedInboundPhone(inboundPhone._id);
                setInboundWarning("");
            }
            // Set outbound phone if agent has one assigned
            if (outboundPhone) {
                setSelectedOutboundPhone(outboundPhone._id);
                setOutboundWarning("");
            }
        }
        // Reset selections when modal closes
        if (!isPhoneModal) {
            setSelectedInboundPhone("");
            setSelectedOutboundPhone("");
            setInboundWarning("");
            setOutboundWarning("");
        }
    }, [isPhoneModal, phones, agentId]);

    // Filter phones based on type
    const availablePhones = phones || [];

    // Filter phones that have trunk IDs (meaning they are configured)
    const inboundPhones = availablePhones.filter(phone => phone.inboundTrunk);
    const outboundPhones = availablePhones.filter(phone => phone.outboundTrunk);

    // If no phones with trunks, show all phones as both types are technically possible
    const finalInboundPhones = inboundPhones.length > 0 ? inboundPhones : availablePhones;
    const finalOutboundPhones = outboundPhones.length > 0 ? outboundPhones : availablePhones;


    const handleInboundChange = (event) => {
        const phoneId = event.target.value;
        setSelectedInboundPhone(phoneId);

        // Check if phone is already assigned to another agent
        const selectedPhone = finalInboundPhones.find(p => p._id === phoneId);
        if (selectedPhone?.inboundAgentId && selectedPhone.inboundAgentId._id !== agentId) {
            const previousAgentName = selectedPhone.inboundAgentId.name || 'another agent';
            setInboundWarning(`Warning: This phone is currently assigned to ${previousAgentName} for inbound calls. Assigning it here will unassign it from the previous agent.`);
        } else setInboundWarning("")
    };

    const handleOutboundChange = (event) => {
        const phoneId = event.target.value;
        setSelectedOutboundPhone(phoneId);

        // Check if phone is already assigned to another agent
        const selectedPhone = finalOutboundPhones.find(p => p._id === phoneId);
        if (selectedPhone?.outboundAgentId && selectedPhone.outboundAgentId._id !== agentId) {
            const previousAgentName = selectedPhone.outboundAgentId.name || 'another agent';
            setOutboundWarning(`Warning: This phone is currently assigned to ${previousAgentName} for outbound calls. Assigning it here will unassign it from the previous agent.`);
        } else setOutboundWarning("")
    };

    const handleAssignPhone = async () => {
        setIsAssigning(true);
        try {
            // Prepare updates for phone assignments - using a Map to handle same phone for both directions
            const updatesMap = new Map();

            // Recalculate phone lists within function scope
            const availablePhones = phones || [];
            const inboundPhones = availablePhones.filter(phone => phone.inboundTrunk);
            const outboundPhones = availablePhones.filter(phone => phone.outboundTrunk);
            const finalInboundPhones = inboundPhones.length > 0 ? inboundPhones : availablePhones;
            const finalOutboundPhones = outboundPhones.length > 0 ? outboundPhones : availablePhones;

            // Handle inbound phone assignment
            if (selectedInboundPhone) {
                const selectedPhone = finalInboundPhones.find(p => p._id === selectedInboundPhone);
                if (selectedPhone) {
                    const existing = updatesMap.get(selectedPhone._id);
                    updatesMap.set(selectedPhone._id, {
                        phoneId: selectedPhone._id,
                        updateData: { ...selectedPhone, ...(existing?.updateData || {}), inboundAgentId: agentId }
                    });
                }
            }

            // Handle outbound phone assignment
            if (selectedOutboundPhone) {
                const selectedPhone = finalOutboundPhones.find(p => p._id === selectedOutboundPhone);
                if (selectedPhone) {
                    const existing = updatesMap.get(selectedPhone._id);
                    updatesMap.set(selectedPhone._id, {
                        phoneId: selectedPhone._id,
                        updateData: { ...selectedPhone, ...(existing?.updateData || {}), outboundAgentId: agentId }
                    });
                }
            }
            // Convert Map to array and perform updates
            const updates = Array.from(updatesMap.values());
            for (const update of updates) {
                dispatch(updatePhone({ _id: update.phoneId, ...update.updateData }));
            }
            // Notify parent component about the phone assignment
            if (onPhoneAssign && updates.length > 0) {
                const assignedPhones = updates.map(update => {
                    const phone = availablePhones.find(p => p._id === update.phoneId);
                    return {
                        phoneNumber: phone?.phoneNumber,
                        inboundAgentId: update.updateData.inboundAgentId,
                        outboundAgentId: update.updateData.outboundAgentId
                    };
                });
                onPhoneAssign(assignedPhones[0]);
            }

            // Show success message
            if (updates.length > 0) {
                const assignmentTypes = [];
                const firstUpdate = updates[0];
                if (firstUpdate.updateData.inboundAgentId) assignmentTypes.push('inbound');
                if (firstUpdate.updateData.outboundAgentId) assignmentTypes.push('outbound');

                const phone = availablePhones.find(p => p._id === firstUpdate.phoneId);
                const phoneDisplay = phone?.nickName || phone?.phoneNumber || 'Phone';

                EventBus.publish('success', `${phoneDisplay} successfully assigned for ${assignmentTypes.join(' and ')} calls`);
            }

            // Reset form and close modal
            setSelectedInboundPhone("");
            setSelectedOutboundPhone("");
            setInboundWarning("");
            setOutboundWarning("");
            togglePhoneModal();

        } catch (error) {
            console.error('Error assigning phone:', error);
        } finally {
            setIsAssigning(false);
        }
    };

    // Commented out: handleClick function - no longer needed without checkboxes
    // const handleClick = (index) => {
    //     if (!activeIndexes.includes(index)) setActiveIndexes([...activeIndexes, index]);
    // };

    // Commented out: templates array - no longer needed since we removed template boxes with checkboxes
    // const templates = [
    //     {
    //         icon: <svg>...</svg>,
    //         title: "Inbound phone number",
    //     },
    //     {
    //         icon: <svg>...</svg>,
    //         title: "Outbound phone number",
    //     },
    // ];

    const handleClose = () => {
        // Reset all state when closing
        setSelectedInboundPhone("");
        setSelectedOutboundPhone("");
        setInboundWarning("");
        setOutboundWarning("");
        togglePhoneModal();
    };


    return (
        <Modal isOpen={isPhoneModal} className={`main-modal new-user-modal phone-assign-modal modal-dialog-centered`}>
            <ModalHeader toggle={handleClose}></ModalHeader>
            <ModalBody>
                <div className='add-content'>
                    <h2>Select Phone Number</h2>
                    <p>Phone number is optional. You can proceed without it.</p>
                    <div className='templates-inner add-phone-modal mt-4'>
                        <div className='account-tabs'>
                            <div className='account-tab-inner'>
                                <div className='right-tabs-area full-width'>
                                    <div className='tabs-content'>
                                        <div className='template-tabs'>
                                            <div className="template-inner">
                                                {/* Bottom section - Display in ROW */}
                                                <div className="bottom-section mt-4">
                                                    <div className='searchbox'>
                                                        <label className="phone-label">
                                                            <span className="phone-label-icon">
                                                                <svg width="20" height="20" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                                                    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                                                        <g fill="currentColor" fillRule="nonzero">
                                                                            <path d="M9.52542875,3.57168189 L10.4270967,5.57740261 C10.9098352,6.65123333 10.6554411,7.91323804 9.79438244,8.71618694 L8.30008193,10.1096425 C8.25640764,10.1503695 8.22875433,10.2053844 8.22212588,10.2647321 C8.17776923,10.6618791 8.44699717,11.4352815 9.06726029,12.5096087 C9.5183106,13.2908508 9.92655784,13.838726 10.2740504,14.1468654 C10.5161536,14.3615508 10.6494298,14.4079831 10.7061653,14.3910897 L12.7164815,13.7764726 C13.8420771,13.4323425 15.0618824,13.8427092 15.7505934,14.7972081 L17.0313263,16.5722024 C17.8374367,17.6894074 17.692473,19.230697 16.692188,20.1780042 L15.8058358,21.0174128 C14.8494043,21.9231817 13.487863,22.2607109 12.2191,21.9065709 C9.46501819,21.1378455 6.99570986,18.8137275 4.78383398,14.9826461 C2.56888664,11.1462447 1.79169454,7.84180015 2.50814642,5.07001095 C2.81780004,3.87199808 3.69676657,2.9086577 4.85022868,2.48678428 L5.05657025,2.41801924 L6.22357049,2.06625527 C7.54607371,1.6676187 8.95907499,2.31185281 9.52542875,3.57168189 Z M6.65647056,3.50242989 L5.48947031,3.85419386 L5.33091122,3.90848982 C4.65595811,4.16804509 4.1430876,4.73866026 3.9604176,5.44538865 C3.35776772,7.77690385 4.04662254,10.7057584 6.08287208,14.2326461 C8.11640238,17.7548239 10.3053483,19.8150642 12.6223688,20.4617961 C13.3836268,20.6742802 14.2005529,20.4717623 14.7744112,19.9283017 L15.6607611,19.0888952 C16.1154361,18.658301 16.1813287,17.9577148 15.8149149,17.4498943 L14.534182,15.6749001 C14.2211315,15.241037 13.6666745,15.0545066 13.1550402,15.2109294 L11.1396992,15.8270635 C9.96998942,16.1757485 8.90831482,15.2343071 7.76822218,13.2596087 C6.99991888,11.9288684 6.64182628,10.9001874 6.73139495,10.098235 C6.77779399,9.68280159 6.97136623,9.29769893 7.27708465,9.01261171 L8.77138576,7.61915559 C9.16277605,7.25417881 9.27840975,6.68054031 9.05898314,6.19243543 L8.15731522,4.18671472 C7.89988169,3.61406514 7.25760838,3.32123145 6.65647056,3.50242989 Z M21.2496358,10.0051867 C21.6638494,10.0051867 21.9996358,9.66940024 21.9996358,9.25518668 L21.9996358,2.75123901 C21.9996358,2.33702544 21.6638494,2.00123901 21.2496358,2.00123901 C20.8354223,2.00123901 20.4996358,2.33702544 20.4996358,2.75123901 L20.4996264,7.44549802 L14.2796546,1.22177337 C13.9867272,0.928845932 13.5118535,0.928845932 13.2189261,1.22177337 C12.9259987,1.51470081 12.9259987,1.98957452 13.2189261,2.28250196 L19.4386264,8.50549802 L14.748109,8.50499981 C14.3338954,8.50499981 13.998109,8.84078625 13.998109,9.25499981 C13.998109,9.66921338 14.3338954,10.0049998 14.748109,10.0049998 L21.2496358,10.0051867 Z" />
                                                                        </g>
                                                                    </g>
                                                                </svg>
                                                            </span>
                                                            Agent's inbound phone number
                                                        </label>
                                                            <FormControl fullWidth variant="outlined" size="small">
                                                                <Select
                                                                    fullWidth
                                                                    value={selectedInboundPhone}
                                                                    onChange={handleInboundChange}
                                                                    displayEmpty
                                                                >
                                                                    <MenuItem value="">Select a phone number</MenuItem>
                                                                    {finalInboundPhones.length > 0 ? (
                                                                        finalInboundPhones.map((phone) => (
                                                                            <MenuItem key={phone._id} value={phone._id}>
                                                                                {phone.nickName || phone.phoneNumber} ({phone.phoneNumber})
                                                                                {phone.inboundAgentId && phone.inboundAgentId._id === agentId && ' - Assigned to this agent'}
                                                                                {phone.inboundAgentId && phone.inboundAgentId._id !== agentId && ' - Assigned to different agent'}
                                                                            </MenuItem>
                                                                        ))
                                                                    ) : (
                                                                        <MenuItem value="" disabled>
                                                                            No inbound phones available
                                                                        </MenuItem>
                                                                    )}
                                                                </Select>
                                                            </FormControl>
                                                            {inboundWarning && (
                                                                <div className="phone-warning">
                                                                    <svg className="phone-warning-icon" viewBox="0 0 16 16" fill="currentColor">
                                                                        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                                                                    </svg>
                                                                    <span>{inboundWarning}</span>
                                                                </div>
                                                            )}
                                                        </div>

                                                    <div className='searchbox'>
                                                        <label className="phone-label">
                                                            <span className="phone-label-icon">
                                                                <svg width="20" height="20" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                                                    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                                                        <g fill="currentColor" fillRule="nonzero">
                                                                            <path d="M9.52542875,3.57168189 L10.4270967,5.57740261 C10.9098352,6.65123333 10.6554411,7.91323804 9.79438244,8.71618694 L8.30008193,10.1096425 C8.25640764,10.1503695 8.22875433,10.2053844 8.22212588,10.2647321 C8.17776923,10.6618791 8.44699717,11.4352815 9.06726029,12.5096087 C9.5183106,13.2908508 9.92655784,13.838726 10.2740504,14.1468654 C10.5161536,14.3615508 10.6494298,14.4079831 10.7061653,14.3910897 L12.7164815,13.7764726 C13.8420771,13.4323425 15.0618824,13.8427092 15.7505934,14.7972081 L17.0313263,16.5722024 C17.8374367,17.6894074 17.692473,19.230697 16.692188,20.1780042 L15.8058358,21.0174128 C14.8494043,21.9231817 13.487863,22.2607109 12.2191,21.9065709 C9.46501819,21.1378455 6.99570986,18.8137275 4.78383398,14.9826461 C2.56888664,11.1462447 1.79169454,7.84180015 2.50814642,5.07001095 C2.81780004,3.87199808 3.69676657,2.9086577 4.85022868,2.48678428 L5.05657025,2.41801924 L6.22357049,2.06625527 C7.54607371,1.6676187 8.95907499,2.31185281 9.52542875,3.57168189 Z M6.65647056,3.50242989 L5.48947031,3.85419386 L5.33091122,3.90848982 C4.65595811,4.16804509 4.1430876,4.73866026 3.9604176,5.44538865 C3.35776772,7.77690385 4.04662254,10.7057584 6.08287208,14.2326461 C8.11640238,17.7548239 10.3053483,19.8150642 12.6223688,20.4617961 C13.3836268,20.6742802 14.2005529,20.4717623 14.7744112,19.9283017 L15.6607611,19.0888952 C16.1154361,18.658301 16.1813287,17.9577148 15.8149149,17.4498943 L14.534182,15.6749001 C14.2211315,15.241037 13.6666745,15.0545066 13.1550402,15.2109294 L11.1396992,15.8270635 C9.96998942,16.1757485 8.90831482,15.2343071 7.76822218,13.2596087 C6.99991888,11.9288684 6.64182628,10.9001874 6.73139495,10.098235 C6.77779399,9.68280159 6.97136623,9.29769893 7.27708465,9.01261171 L8.77138576,7.61915559 C9.16277605,7.25417881 9.27840975,6.68054031 9.05898314,6.19243543 L8.15731522,4.18671472 C7.89988169,3.61406514 7.25760838,3.32123145 6.65647056,3.50242989 Z M14.748109,2.00123901 L21.2639579,2.00143024 C21.2897403,2.00062323 21.3155601,2.00243916 21.3412485,2.0055826 L21.4027749,2.01586794 L21.4027749,2.01586794 L21.49785,2.04223885 L21.49785,2.04223885 L21.5864739,2.07983024 L21.5864739,2.07983024 L21.6538782,2.11945215 L21.6538782,2.11945215 C21.6984288,2.14662311 21.7407476,2.18047181 21.7798294,2.21953348 C21.8183322,2.25801646 21.851777,2.29964391 21.8801634,2.34358965 L21.9222379,2.41771652 L21.9222379,2.41771652 L21.9640173,2.52110163 L21.9640173,2.52110163 L21.9880802,2.61848045 L21.9880802,2.61848045 L21.9990152,2.71940235 L21.9990152,2.71940235 L21.9996358,9.25518668 C21.9996358,9.66940024 21.6638494,10.0051867 21.2496358,10.0051867 C20.86994,10.0051867 20.5561448,9.7230328 20.5064824,9.35695724 L20.4996358,9.25518668 L20.4996264,4.56079802 L14.2796546,10.7845019 C14.0134565,11.0508369 13.5967991,11.0751502 13.3031316,10.8573713 L13.2189944,10.7847748 C12.9526594,10.5185767 12.9283461,10.1019193 13.146125,9.80825175 L13.2187216,9.72411464 L19.4386264,3.50079802 L14.748109,3.50129623 C14.3684132,3.50129623 14.054618,3.21914235 14.0049556,2.85306678 L13.998109,2.75129623 C13.998109,2.33708266 14.3338954,2.00123901 14.748109,2.00123901 Z" />
                                                                        </g>
                                                                    </g>
                                                                </svg>
                                                            </span>
                                                            Agent's outbound phone number
                                                        </label>
                                                            <FormControl fullWidth variant="outlined" size="small">
                                                                <Select
                                                                    fullWidth
                                                                    value={selectedOutboundPhone}
                                                                    onChange={handleOutboundChange}
                                                                    displayEmpty
                                                                >
                                                                    <MenuItem value="">Select a phone number</MenuItem>
                                                                    {finalOutboundPhones.length > 0 ? (
                                                                        finalOutboundPhones.map((phone) => (
                                                                            <MenuItem key={phone._id} value={phone._id}>
                                                                                {phone.nickName || phone.phoneNumber} ({phone.phoneNumber})
                                                                                {phone.outboundAgentId && phone.outboundAgentId._id === agentId && ' - Assigned to this agent'}
                                                                                {phone.outboundAgentId && phone.outboundAgentId._id !== agentId && ' - Assigned to different agent'}
                                                                            </MenuItem>
                                                                        ))
                                                                    ) : (
                                                                        <MenuItem value="" disabled>
                                                                            No outbound phones available
                                                                        </MenuItem>
                                                                    )}
                                                                </Select>
                                                            </FormControl>
                                                            {outboundWarning && (
                                                                <div className="phone-warning">
                                                                    <svg className="phone-warning-icon" viewBox="0 0 16 16" fill="currentColor">
                                                                        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                                                                    </svg>
                                                                    <span>{outboundWarning}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                </div>

                                                <div className="btn-box mt-4">
                                                    <button className="btn-style-border" onClick={handleClose}>Close</button>
                                                    <button
                                                        className="btn-style-new"
                                                        onClick={handleAssignPhone}
                                                        disabled={isAssigning || (!selectedInboundPhone && !selectedOutboundPhone)}
                                                    >
                                                        {isAssigning ? 'Assigning...' : 'Assign Phone to Agent'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default AddAgentModal;